/**
 * F13 Dashboard — types voor backoffice `/dashboard` (3-tab admin overzicht).
 *
 * Geen API-aggregator-DTO's — de UI haalt per widget bestaande stats-endpoints
 * op (zie `docs/modules/dashboard.md` voor de widget×endpoint-tabel). Dit
 * bestand bevat alleen UI-helper types die niet in een module-DTO horen.
 */

import { z } from 'zod'

// ─── Tab-id's ─────────────────────────────────────────────────────────────────

export const DASHBOARD_TABS = ['operatie', 'rendement', 'capaciteit'] as const
export type DashboardTab = (typeof DASHBOARD_TABS)[number]

export const dashboardTabSchema = z.enum(DASHBOARD_TABS)

export const DASHBOARD_TAB_LABELS: Record<DashboardTab, string> = {
  operatie:   'Dashboard Operatie',
  rendement:  'Dashboard Rendement',
  capaciteit: 'Dashboard Capaciteit',
}

// ─── Aandachtspunten ──────────────────────────────────────────────────────────

/**
 * Eén regel in de "Aandachtspunten"-card op de Operatie-tab. Wordt client-side
 * berekend uit losse stats-responses; geen eigen endpoint nodig.
 */
export interface DashboardAlert {
  id: string
  type: 'error' | 'warning' | 'info'
  title: string
  description: string
  /** Deeplink naar de module waar de gebruiker actie kan ondernemen. */
  link: string
  /** Lucide-icon naam (UI mapt naar component). */
  iconName:
    | 'alert-circle'
    | 'clock'
    | 'calendar'
    | 'check-circle'
    | 'file-warning'
    | 'wrench'
}

// ─── Bezettings-aggregator (fase 5.3) ─────────────────────────────────────

export const BEZETTING_PERIODES = ['week', 'maand', 'kwartaal'] as const
export type BezettingPeriode = (typeof BEZETTING_PERIODES)[number]

export const bezettingPeriodeSchema = z.enum(BEZETTING_PERIODES)

export const bezettingQuerySchema = z.object({
  periode: bezettingPeriodeSchema.default('week'),
  /** Aantal periodes terug in de tijd, inclusief de huidige (1-26). */
  count: z.coerce.number().int().min(1).max(26).default(4),
  /** Optioneel filter op één entiteit (BV) via `Employee.entityId`. */
  entityId: z.string().min(1).optional(),
})
export type BezettingQuery = z.infer<typeof bezettingQuerySchema>

/**
 * Eén rij in de bezettingsgraad-trend op de Capaciteit-tab.
 *
 * `bezettingPercentage` = `planningUren / beschikbareUren × 100`, of 0 als
 * `beschikbareUren = 0`. Kan > 100 zijn bij overplanning.
 *
 * Ronde 4 W8 (spec 08 §4.1) — `beschikbareUren`/`planningUren` komen nu uit de
 * capaciteits-engine (verlof/verzuim/feestdag afgetrokken), niet meer uit
 * platte contracturen × Planning-blokken. De optionele `verlofUren`/
 * `verzuimUren`/`feestdagUren`/`restCapaciteit` zijn additief (bestaande
 * clients negeren ze) en voeden de gestapelde bezettingsgrafiek.
 */
export interface BezettingRowDTO {
  periode: BezettingPeriode
  label: string
  start: string // ISO datetime
  end: string // ISO datetime
  beschikbareUren: number
  planningUren: number
  bezettingPercentage: number
  // ── W8-uitbreiding (optioneel, additief) ──
  verlofUren?: number
  verzuimUren?: number
  feestdagUren?: number
  restCapaciteit?: number
}

export interface BezettingResponseDTO {
  rows: BezettingRowDTO[]
  /** Aantal medewerkers dat in de berekening is meegenomen (na entiteit-filter). */
  aantalMedewerkers: number
  /** Optioneel: gefilterde entiteit-naam, voor UI-banner. */
  entityName: string | null
}

// ─── Capaciteit-detail (Ronde 4 W8 spec 08 §4.2) ──────────────────────────────
//
// De request-query (`capacityDetailQuerySchema`/`CapacityDetailQuery`) staat in
// `capacity.ts` (één bron) en wordt door de dashboard-route hergebruikt — niet
// hier hergedefinieerd om een dubbel-export-conflict te voorkomen. Hieronder
// alleen de dashboard-specifieke response-DTO's.

/**
 * Kleurcode per medewerker-rij op de Capaciteit-tabel (spec §4.2). Aparte,
 * compactere 4-waarden-enum dan de planbord-`CapacityTone` (5 waarden) — de
 * dashboard-tabel kent geen apart "niet-beschikbaar" (die rijen vallen onder
 * `under`/`ok` met 0 beschikbare uren). De route mapt `CapacityTone` → dit.
 */
export type CapacityRowTone = 'ok' | 'warn' | 'over' | 'under'

export interface CapacityDetailTotalenDTO {
  contractUren: number
  beschikbareUren: number
  geplandeUren: number
  verlofUren: number
  verzuimUren: number
  feestdagUren: number
  restCapaciteit: number
  bezettingsgraad: number
  /** `null` zolang geen enkele medewerker een bekend salaris heeft. */
  geschatteLeegloopKosten: number | null
}

export interface CapacityDetailRowDTO {
  id: string
  naam: string
  functie: string | null
  entityId: string | null
  entityNaam: string | null
  contractUren: number
  beschikbareUren: number
  geplandeUren: number
  restCapaciteit: number
  bezettingsgraad: number
  tone: CapacityRowTone
  verzuimDagen: number
  verlofDagen: number
  /** `null` indien `grossMonthlySalary` onbekend (nooit gegokt). */
  uurkosten: number | null
  leegloopKosten: number | null
}

export interface CapacityDetailSignaalDTO {
  type: 'OVERBELAST' | 'BIJNA_VOL' | 'ONDERBENUT' | 'STRUCTUREEL_OVERWERK' | 'HOOG_VERZUIM' | 'KOSTEN_LEEGLOOP'
  ernst: 'error' | 'warning' | 'info'
  bericht: string
  medewerkerIds: string[]
}

/**
 * Top-onderbenutter met geschatte leegloopkosten (spec §7.2 kosten-widget).
 * Alleen aanwezig in de response voor users met financiële rechten
 * (PAYROLL.canView) — anders wordt het hele blok weggelaten (zie
 * `CapacityDetailResponseDTO.topOnderbenut`).
 */
export interface CapacityUnderutilizerDTO {
  id: string
  naam: string
  bezettingsgraad: number
  restCapaciteit: number
  /** `null` indien `grossMonthlySalary` onbekend (nooit gegokt). */
  geschatteLeegloopKosten: number | null
}

export interface CapacityDetailResponseDTO {
  periode: { start: string; end: string; label: string }
  totalen: CapacityDetailTotalenDTO
  medewerkers: CapacityDetailRowDTO[]
  signalen: CapacityDetailSignaalDTO[]
  /**
   * Top-5 onderbenutte medewerkers met geschatte leegloopkosten (spec §7.2).
   * `null` voor users zonder PAYROLL.canView (financiële afscherming) — de
   * frontend toont het kosten-widget dan niet.
   */
  topOnderbenut: CapacityUnderutilizerDTO[] | null
}

// ─── Capaciteit-trend (Ronde 4 W8 spec 08 §10.2) ──────────────────────────────
//
// `GET /dashboard/capacity-trend?entityId=&weeks=12` leest de wekelijkse
// `CapacitySnapshot`-rijen (cron Fase 10) voor de bezettingslijn op de
// Capaciteit-tab. Team-breed = `entityId` weggelaten (snapshot-rij met
// `entityId = null`); met `entityId` één BV.

export const capacityTrendQuerySchema = z.object({
  /** Aantal recente weken (1-52). Default 12 (spec §10.2). */
  weeks: z.coerce.number().int().min(1).max(52).default(12),
  /** Optioneel filter op één entiteit (BV). Weggelaten = team-breed. */
  entityId: z.string().min(1).optional(),
})
export type CapacityTrendQuery = z.infer<typeof capacityTrendQuerySchema>

/**
 * Eén meetpunt in de bezettings-trend (één wekelijkse snapshot). `date` is de
 * maandag van de ISO-week (UTC). `bezettingsgraad` = `geplandeUren /
 * beschikbareUren × 100`. `leegloopKosten` is `null` zonder PAYROLL.canView
 * (financiële afscherming) of wanneer geen salaris bekend was.
 */
export interface CapacityTrendPointDTO {
  date: string // ISO datetime — maandag van de week
  /** Leesbaar weeklabel, bv. "W21". */
  label: string
  beschikbareUren: number
  geplandeUren: number
  verlofUren: number
  verzuimUren: number
  restCapaciteit: number
  bezettingsgraad: number
  leegloopKosten: number | null
}

export interface CapacityTrendResponseDTO {
  /** Chronologisch (oudste eerst), max `weeks` punten. */
  punten: CapacityTrendPointDTO[]
  /** Gefilterde entiteit-naam, of `null` voor team-breed. */
  entityName: string | null
  /** Aantal beschikbare meetpunten (kan < weeks zijn op een verse omgeving). */
  aantalMeetpunten: number
}
